import type { DeviceWorkBackend } from './durable-work-device';

/**
 * Work that outlives a screen: a capture waiting for signal, a refund whose answer never came back, a parked edit.
 *
 * Every entry belongs to one principal, tenant, restaurant and kind, and no one else reads it through `list`. A kind says
 * what happens when its owner's session ends (`whenOwnerLeaves`, Identity's `purge` at logout and at a change of person):
 *
 * - `purge`: a cache or a result that can be had again goes, from every tier;
 * - `wait`: **work that has not reached the server (money, a document, a refund) is never removed by anyone leaving.** A
 *   shift change on a shared TPV is ordinary (docs/decisions/2026-10-31-tpv-offline-without-a-caja-tablet.md): it stays,
 *   its owner finds it at the next sign-in, and the next person sees it in `waitingOfOthers`. Only `remove`, or `done` from
 *   `drain`, ends it.
 *
 * A kind also chooses its tier and its time to live:
 *
 * - `memory`: as long as the page is open;
 * - `session`: as long as the tab, in `sessionStorage` (read synchronously, for what a screen needs on its first draw);
 * - `device`: on the device, in the Panel's one IndexedDB database (`./durable-work-device.ts`, a chunk of its own).
 *
 * Storage the browser refuses (a private window, a full quota) never loses the work while the page is open: the entry
 * stays in memory, and `persistence()` says where the kind's work really lives, so a screen can say so.
 *
 * A parked slot holds the one live object a leaving screen could not finish (its edit and its retained command) until
 * the next mount of the same key adopts it. Identity's purge closes it.
 */

type DurableWorkTier = 'memory' | 'session' | 'device';
type DurableWorkPersistence = DurableWorkTier;

export interface DurableWorkScope {
  readonly principalId: string;
  readonly tenantId: string;
  readonly restaurantId: string;
}

export interface DurableWorkKind<Item, Tier extends DurableWorkTier = DurableWorkTier> {
  readonly kind: string;
  readonly tier: Tier;
  /** What a logout or another person's sign-in does to its entries. Unsent money or documents: `wait`. */
  readonly whenOwnerLeaves: 'purge' | 'wait';
  /** How long an entry lives, the instant it ends for a given creation time (the end of a service day), or null: until it is done. */
  readonly ttlMs: number | ((createdAt: number) => number) | null;
  /** The waits between attempts of `drain`; the last one repeats. No policy: every drain tries again. */
  readonly retryDelaysMs?: readonly number[];
  /** Whether a stored value is still an item (another build may have written it). Refused values are dropped. */
  readonly accepts?: (value: unknown) => value is Item;
}

interface DurableWorkEntry<Item> {
  readonly id: string;
  readonly item: Item;
  readonly createdAt: number;
  readonly expiresAt: number;
  readonly attempts: number;
  readonly nextAttemptAt: number;
}

/** What one `drain` send answers: it landed, it failed and waits for its next attempt, or nothing more can go now. */
type DurableWorkSendOutcome = 'done' | 'retry' | 'stop';

interface DurableWorkDrainResult {
  readonly done: number;
  /** Entries still waiting after this drain. */
  readonly waiting: number;
  /** When the next waiting entry is due, or null when none waits. */
  readonly nextAttemptAt: number | null;
}

interface DurableWork<Item> {
  /** Where this kind's work lives right now: its tier, or `memory` when the browser refused that tier. */
  persistence(): Promise<DurableWorkPersistence>;
  /** Adds or replaces an entry (a replacement keeps its creation and attempts). False once purged: nothing was kept. */
  put(id: string, item: Item): Promise<boolean>;
  get(id: string): Promise<DurableWorkEntry<Item> | null>;
  /** The live entries of this scope, oldest first. */
  list(): Promise<readonly DurableWorkEntry<Item>[]>;
  /** The entries of this kind and restaurant that others left waiting: what the next person on the device must not lose. */
  waitingOfOthers(): Promise<readonly Readonly<{ principalId: string; entry: DurableWorkEntry<Item> }>[]>;
  remove(id: string): Promise<void>;
  /** Sends every due entry, oldest first, one at a time. A thrown send counts as `retry`. One drain per kind at a time. */
  drain(send: (entry: DurableWorkEntry<Item>) => Promise<DurableWorkSendOutcome>): Promise<DurableWorkDrainResult>;
  /** Runs after every change of this kind's entries in this page. */
  subscribe(listener: () => void): () => void;
}

/** The memory and session tiers also answer at once. */
export interface ImmediateDurableWork<Item> extends DurableWork<Item> {
  entriesNow(): readonly DurableWorkEntry<Item>[];
  getNow(id: string): DurableWorkEntry<Item> | null;
  putNow(id: string, item: Item): boolean;
  removeNow(id: string): void;
}

/** @public What a parked slot holds (the Menú outbox, the Constructor scope). `key` names the owner (port, principal, tenant, restaurant); `close` disposes it for good. */
export interface ParkedWork {
  readonly key: string;
  readonly close: () => void;
}

interface ParkedWorkSlot<Work extends ParkedWork> {
  /** Every mount takes the slot: the same key gets its work back, any other key closes it. Retires leaves in flight. */
  adopt(key: string): Work | null;
  /** The ticket a leaving screen must still hold for its park to be accepted. */
  ticket(): number;
  /** Parks leaving work; false (the caller closes it) when a newer mount took over or a purge came first. */
  park(work: Work, ticket: number): boolean;
}

/** Keep the purgeable work of this person (a change of person) or of nobody (a logout). Work that waits always stays. */
interface DurableWorkPurge {
  readonly keepPrincipalId: string | null;
}

export interface DurableWorkStore {
  open<Item>(scope: DurableWorkScope, kind: DurableWorkKind<Item, 'memory' | 'session'>): ImmediateDurableWork<Item>;
  open<Item>(scope: DurableWorkScope, kind: DurableWorkKind<Item, 'device'>): DurableWork<Item>;
  /** The kind's one parked slot in this page. */
  parkedSlot<Work extends ParkedWork>(kind: string): ParkedWorkSlot<Work>;
  /** Removes others' purgeable work from every tier and closes every parked slot. Never touches work that waits. Never rejects. */
  purge(request: DurableWorkPurge): Promise<void>;
}

type SessionStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

interface DurableWorkStoreOptions {
  /** The tab's storage; null where the browser refuses it. */
  readonly session?: () => SessionStorageLike | null;
  /** The device's databases; null where there are none. */
  readonly indexedDb?: () => IDBFactory | null;
  readonly now?: () => number;
}

const SESSION_PREFIX = 'exylon.panel.work.v1:';

/** The queue's name in every tier: it says who owns it and whether a purge may remove it, read with no kind at hand. */
const queueKey = (scope: DurableWorkScope, kind: Pick<DurableWorkKind<unknown>, 'kind' | 'whenOwnerLeaves'>): string =>
  JSON.stringify([scope.principalId, scope.tenantId, scope.restaurantId, kind.kind, kind.whenOwnerLeaves]);
const partsOfQueue = (queue: string): readonly unknown[] => {
  try {
    const parsed = JSON.parse(queue) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
/** Forever, for an entry with no time to live, in a number that survives JSON. */
const NO_END = Number.MAX_SAFE_INTEGER;

const isEntry = (value: unknown): value is DurableWorkEntry<unknown> => {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  return typeof entry.id === 'string' && 'item' in entry && typeof entry.createdAt === 'number'
    && typeof entry.expiresAt === 'number' && typeof entry.attempts === 'number' && typeof entry.nextAttemptAt === 'number';
};

function browserSession(): SessionStorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}
function browserIndexedDb(): IDBFactory | null {
  try {
    return typeof indexedDB === 'undefined' ? null : indexedDB;
  } catch {
    return null;
  }
}

/** @internal Exported for its spec; the Panel uses `panelDurableWork`. */
export function createDurableWorkStore(options: DurableWorkStoreOptions = {}): DurableWorkStore {
  const session = options.session ?? browserSession;
  const indexedDbFactory = options.indexedDb ?? browserIndexedDb;
  const now = options.now ?? Date.now;
  /** The memory tier, and what another tier refused. */
  const memory = new Map<string, Map<string, DurableWorkEntry<unknown>>>();
  const queues = new Map<string, DurableWork<unknown> & { retire(): void }>();
  const slots = new Map<string, ParkedWorkSlot<ParkedWork> & { purge(): void }>();
  let backend: Promise<DeviceWorkBackend | null> | null = null;
  const device = (): Promise<DeviceWorkBackend | null> => {
    if (backend === null) {
      const factory = indexedDbFactory();
      backend = factory === null ? Promise.resolve(null) : import('./durable-work-device').then((module) => module.openDeviceWorkBackend(factory));
      // A chunk that did not load (no connection) is asked for again next time; the work waits in memory.
      backend = backend.catch(() => { backend = null; return null; });
    }
    return backend;
  };
  const held = (queue: string): Map<string, DurableWorkEntry<unknown>> => {
    let entries = memory.get(queue);
    if (entries === undefined) {
      entries = new Map();
      memory.set(queue, entries);
    }
    return entries;
  };

  const readSession = (queue: string): DurableWorkEntry<unknown>[] => {
    try {
      const raw = session()?.getItem(SESSION_PREFIX + queue);
      if (raw === null || raw === undefined) return [];
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
    } catch {
      return [];
    }
  };
  /** False when the tab refused the write: the caller keeps the entries in memory. */
  const writeSession = (queue: string, entries: readonly DurableWorkEntry<unknown>[]): boolean => {
    const storage = session();
    if (storage === null) return false;
    try {
      if (entries.length === 0) storage.removeItem(SESSION_PREFIX + queue);
      else storage.setItem(SESSION_PREFIX + queue, JSON.stringify(entries));
      return true;
    } catch {
      return false;
    }
  };

  const openQueue = <Item,>(scope: DurableWorkScope, kind: DurableWorkKind<Item>): ImmediateDurableWork<Item> & { retire(): void } => {
    const queue = queueKey(scope, kind as DurableWorkKind<unknown>);
    const waits = kind.whenOwnerLeaves === 'wait';
    const listeners = new Set<() => void>();
    let retired = false;
    let refusedWrite = false;
    let draining: Promise<DurableWorkDrainResult> | null = null;
    const changed = () => { for (const listener of [...listeners]) listener(); };
    const expiresAt = (createdAt: number) => kind.ttlMs === null ? NO_END : typeof kind.ttlMs === 'function' ? kind.ttlMs(createdAt) : createdAt + kind.ttlMs;
    const live = (entry: DurableWorkEntry<unknown>, at: number): entry is DurableWorkEntry<Item> =>
      entry.expiresAt > at && (kind.accepts === undefined || kind.accepts(entry.item));
    const sorted = (entries: Iterable<DurableWorkEntry<Item>>) =>
      [...entries].sort((a, b) => a.createdAt - b.createdAt);
    const next = (id: string, item: Item, previous: DurableWorkEntry<Item> | null | undefined): DurableWorkEntry<Item> => {
      const createdAt = previous?.createdAt ?? now();
      return { id, item, createdAt, expiresAt: previous?.expiresAt ?? expiresAt(createdAt),
        attempts: previous?.attempts ?? 0, nextAttemptAt: previous?.nextAttemptAt ?? createdAt };
    };

    /*
     * Memory and session: answered at once. After a write the tab refused, memory holds the whole queue and the session
     * still holds an older copy of it, so memory alone answers: what was removed since does not come back.
     */
    const immediateEntries = (): DurableWorkEntry<Item>[] => {
      const at = now();
      const stored = memory.get(queue)?.values() ?? (kind.tier === 'session' ? readSession(queue) : []);
      return sorted([...stored].filter((entry): entry is DurableWorkEntry<Item> => live(entry, at)));
    };
    const storeImmediate = (entries: readonly DurableWorkEntry<Item>[]) => {
      if (kind.tier === 'session' && writeSession(queue, entries)) {
        refusedWrite = false;
        memory.delete(queue);
        return;
      }
      if (kind.tier === 'session') refusedWrite = true;
      memory.set(queue, new Map(entries.map((entry) => [entry.id, entry])));
    };
    const putNow = (id: string, item: Item): boolean => {
      if (retired) return false;
      const entries = immediateEntries();
      const previous = entries.find((entry) => entry.id === id);
      storeImmediate(sorted([...entries.filter((entry) => entry.id !== id), next(id, item, previous)]));
      changed();
      return true;
    };
    const removeNow = (id: string): void => {
      const entries = immediateEntries();
      if (!entries.some((entry) => entry.id === id)) return;
      storeImmediate(entries.filter((entry) => entry.id !== id));
      changed();
    };

    /* Device: the database, and memory for what it refused. */
    const rowKey = (id: string) => `${queue}\u0000${id}`;
    const deviceEntries = async (): Promise<DurableWorkEntry<Item>[]> => {
      const at = now();
      const merged = new Map<string, DurableWorkEntry<unknown>>();
      const handle = await device();
      if (handle !== null) {
        const rows = await handle.list(queue);
        const usable = (row: (typeof rows)[number]): row is (typeof rows)[number] & { entry: DurableWorkEntry<unknown> } => isEntry(row.entry) && live(row.entry, at);
        await handle.remove(rows.filter((row) => !usable(row)).map((row) => row.key));
        for (const row of rows) if (usable(row)) merged.set(row.entry.id, row.entry);
      }
      for (const [id, entry] of held(queue)) if (live(entry, at)) merged.set(id, entry);
      return sorted([...merged.values()] as DurableWorkEntry<Item>[]);
    };
    /** One entry, for writing over it: a write's cost never grows with what else waits in the queue. */
    const deviceEntry = async (id: string): Promise<DurableWorkEntry<Item> | null> => {
      const at = now();
      const inMemory = held(queue).get(id);
      if (inMemory !== undefined && live(inMemory, at)) return inMemory as DurableWorkEntry<Item>;
      const handle = await device();
      const row = handle === null ? null : await handle.get(rowKey(id));
      return row !== null && isEntry(row.entry) && live(row.entry, at) ? row.entry as DurableWorkEntry<Item> : null;
    };
    const storeDevice = async (entry: DurableWorkEntry<Item>): Promise<void> => {
      const handle = await device();
      if (handle !== null && await handle.put({ key: rowKey(entry.id), queue, principalId: scope.principalId, waits, entry })) {
        held(queue).delete(entry.id);
        refusedWrite = false;
        return;
      }
      refusedWrite = true;
      held(queue).set(entry.id, entry);
    };

    const isDevice = kind.tier === 'device';
    const entriesOf = (): Promise<DurableWorkEntry<Item>[]> => isDevice ? deviceEntries() : Promise.resolve(immediateEntries());
    const replace = async (entry: DurableWorkEntry<Item>): Promise<void> => {
      if (isDevice) await storeDevice(entry);
      else storeImmediate(sorted([...immediateEntries().filter((other) => other.id !== entry.id), entry]));
      changed();
    };
    const removeEntry = async (id: string): Promise<void> => {
      if (!isDevice) { removeNow(id); return; }
      held(queue).delete(id);
      const handle = await device();
      await handle?.remove([rowKey(id)]);
      changed();
    };

    return {
      async persistence() {
        if (kind.tier === 'memory') return 'memory';
        if (refusedWrite) return 'memory';
        if (kind.tier === 'session') return session() === null ? 'memory' : 'session';
        return await device() === null ? 'memory' : 'device';
      },
      async put(id, item) {
        if (retired) return false;
        if (!isDevice) return putNow(id, item);
        const previous = await deviceEntry(id);
        // The person may have left while the database answered: nothing of theirs is written after the purge.
        if (retired) return false;
        await storeDevice(next(id, item, previous));
        changed();
        return true;
      },
      async get(id) {
        return (await entriesOf()).find((entry) => entry.id === id) ?? null;
      },
      list: entriesOf,
      async waitingOfOthers() {
        if (!waits) return [];
        const at = now();
        const mine = (other: readonly unknown[]) => other[1] === scope.tenantId && other[2] === scope.restaurantId
          && other[3] === kind.kind && other[4] === 'wait' && other[0] !== scope.principalId;
        const found: { principalId: string; entry: DurableWorkEntry<Item> }[] = [];
        const add = (owner: unknown, entry: unknown) => {
          if (typeof owner === 'string' && isEntry(entry) && live(entry, at)) found.push({ principalId: owner, entry });
        };
        if (isDevice) {
          const handle = await device();
          for (const row of await handle?.all() ?? []) {
            const other = partsOfQueue(row.queue);
            if (mine(other)) add(other[0], row.entry);
          }
        }
        if (kind.tier === 'session') {
          const storage = session();
          for (let index = 0; storage !== null && index < storage.length; index += 1) {
            const key = storage.key(index);
            if (!key?.startsWith(SESSION_PREFIX)) continue;
            const other = partsOfQueue(key.slice(SESSION_PREFIX.length));
            if (mine(other) && !memory.has(key.slice(SESSION_PREFIX.length))) for (const entry of readSession(key.slice(SESSION_PREFIX.length))) add(other[0], entry);
          }
        }
        for (const [other, entries] of memory) {
          const parts = partsOfQueue(other);
          if (mine(parts)) for (const entry of entries.values()) add(parts[0], entry);
        }
        return found.sort((a, b) => a.entry.createdAt - b.entry.createdAt);
      },
      remove: removeEntry,
      drain(send) {
        if (draining !== null) return draining;
        draining = (async () => {
          let done = 0;
          let stopped = false;
          for (const entry of await entriesOf()) {
            if (stopped || retired) break;
            if (entry.nextAttemptAt > now()) continue;
            let outcome: DurableWorkSendOutcome;
            try { outcome = await send(entry); } catch { outcome = 'retry'; }
            if (outcome === 'stop') { stopped = true; continue; }
            if (retired) break;
            if (outcome === 'done') {
              await removeEntry(entry.id);
              done += 1;
              continue;
            }
            const delays = kind.retryDelaysMs ?? [];
            const delay = delays.length === 0 ? 0 : delays[Math.min(entry.attempts, delays.length - 1)]!;
            await replace({ ...entry, attempts: entry.attempts + 1, nextAttemptAt: now() + delay });
          }
          const waiting = await entriesOf();
          return { done, waiting: waiting.length,
            nextAttemptAt: waiting.length === 0 ? null : Math.min(...waiting.map((entry) => entry.nextAttemptAt)) };
        })().finally(() => { draining = null; });
        return draining;
      },
      subscribe(listener) {
        listeners.add(listener);
        return () => { listeners.delete(listener); };
      },
      entriesNow: immediateEntries,
      getNow: (id) => immediateEntries().find((entry) => entry.id === id) ?? null,
      putNow,
      removeNow,
      retire() {
        retired = true;
        changed();
      },
    };
  };

  const createSlot = <Work extends ParkedWork>(): ParkedWorkSlot<Work> & { purge(): void } => {
    let parked: Work | null = null;
    let generation = 0;
    return {
      adopt(key) {
        generation += 1;
        const work = parked;
        parked = null;
        if (work === null) return null;
        if (work.key === key) return work;
        work.close();
        return null;
      },
      ticket: () => generation,
      park(work, ticket) {
        if (ticket !== generation) return false;
        parked?.close();
        parked = work;
        return true;
      },
      purge() {
        generation += 1;
        const work = parked;
        parked = null;
        work?.close();
      },
    };
  };

  const open = <Item,>(scope: DurableWorkScope, kind: DurableWorkKind<Item>) => {
    const queue = queueKey(scope, kind as DurableWorkKind<unknown>);
    const existing = queues.get(queue);
    if (existing !== undefined) return existing as unknown as ImmediateDurableWork<Item>;
    const created = openQueue(scope, kind);
    queues.set(queue, created as unknown as DurableWork<unknown> & { retire(): void });
    return created;
  };

  return {
    open: open as DurableWorkStore['open'],
    parkedSlot<Work extends ParkedWork>(kind: string) {
      let slot = slots.get(kind);
      if (slot === undefined) {
        slot = createSlot<ParkedWork>();
        slots.set(kind, slot);
      }
      return slot as unknown as ParkedWorkSlot<Work>;
    },
    async purge({ keepPrincipalId }) {
      const foreign = (queue: string) => {
        const [owner, , , , whenOwnerLeaves] = partsOfQueue(queue);
        return whenOwnerLeaves !== 'wait' && (keepPrincipalId === null || owner !== keepPrincipalId);
      };
      for (const slot of slots.values()) slot.purge();
      for (const [queue, handle] of [...queues]) {
        if (!foreign(queue)) continue;
        handle.retire();
        queues.delete(queue);
      }
      for (const queue of [...memory.keys()]) if (foreign(queue)) memory.delete(queue);
      const storage = session();
      if (storage !== null) {
        try {
          const keys: string[] = [];
          for (let index = 0; index < storage.length; index += 1) {
            const key = storage.key(index);
            if (key?.startsWith(SESSION_PREFIX) && foreign(key.slice(SESSION_PREFIX.length))) keys.push(key);
          }
          for (const key of keys) storage.removeItem(key);
        } catch { /* A refused storage holds nothing of ours. */ }
      }
      const handle = await device();
      await handle?.purge(keepPrincipalId);
    },
  };
}

/** The Panel's durable work: one per page. */
export const panelDurableWork: DurableWorkStore = createDurableWorkStore();
